import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { cardNumberSelector, networkSelector } from "store/userSliceHelpers";
import { useCardInfo } from "features/cards/useCardInfo";
import { useCardBalance } from "features/cards/useCardBalance";
import { useCardTransactions } from "features/cards/useCardTransactions";

export type TxFilter = "all" | "in" | "out";

export interface UseUserPageReturn {
  cardNumber: string | undefined;
  cardData: any;
  cardBalance: number;
  cardTransactions: any[];
  network: any;
  showLoadMore: boolean;

  /**
   * The card doesn't resolve on the selected network — cards live in a
   * per-network namespace, so a testnet-only card returns nothing on mainnet.
   * Drives the empty state rather than a fallback to the other network.
   */
  isCardMissing: boolean;
  isCardLoading: boolean;
  isBalanceLoading: boolean;
  isTxLoading: boolean;

  txFilter: TxFilter;
  setTxFilter: (filter: TxFilter) => void;
  filteredTransactions: any[];

  currentTransaction: any | undefined;
  showTransactionDetail: boolean;
  openTransactionDetail: (transaction: any) => void;
  closeTransactionDetail: () => void;

  showQrCode: boolean;
  openQrCode: () => void;
  closeQrCode: () => void;

  copied: boolean;
  handleCopy: () => void;
}

export function useUserPage(): UseUserPageReturn {
  const cardNumber = useSelector(cardNumberSelector);
  const network = useSelector(networkSelector);

  const {
    data: cardData,
    isLoading: isCardLoading,
    isError: isCardError,
  } = useCardInfo(cardNumber);
  const { data: cardBalance = 0, isLoading: isBalanceLoading } = useCardBalance(
    cardNumber,
    cardData?.cardAlias,
  );
  const { data: txData, isLoading: isTxLoading } = useCardTransactions(
    cardData?.cardAlias,
  );

  const cardTransactions = txData?.transactions ?? [];
  const showLoadMore = txData?.more ?? false;

  // A missing card and a failed lookup are the same thing to the user: there is
  // nothing to show on this network.
  const isCardMissing =
    !!cardNumber && !isCardLoading && (isCardError || !cardData?.cardAlias);

  // Both of these queries are enabled-gated on the card's alias, so they sit
  // disabled while cardInfo is still in flight — and in react-query v5 a
  // disabled query is pending but *not* fetching, so its isLoading is false.
  // Fold the cardInfo load in, or the UI renders a 0.00 balance and an empty
  // transaction list during the window before they're even allowed to run.
  const isBalanceResolving = isCardLoading || isBalanceLoading;
  const isTxResolving = isCardLoading || isTxLoading;

  const [txFilter, setTxFilter] = useState<TxFilter>("all");
  const filteredTransactions =
    txFilter === "all"
      ? cardTransactions
      : cardTransactions.filter((tx: any) => tx.dir === txFilter);

  const [currentTransaction, setCurrentTransaction] = useState<any>();
  const [showTransactionDetail, setShowTransactionDetail] = useState(false);

  const openTransactionDetail = (transaction: any) => {
    setCurrentTransaction(transaction);
    setShowTransactionDetail(true);
  };
  const closeTransactionDetail = () => setShowTransactionDetail(false);

  const [showQrCode, setShowQrCode] = useState(false);
  const openQrCode = () => setShowQrCode(true);
  const closeQrCode = () => setShowQrCode(false);

  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  const handleCopy = () => {
    navigator.clipboard.writeText(cardData?.cardAlias ?? "");
    setCopied(true);
  };

  return {
    cardNumber,
    cardData,
    cardBalance,
    cardTransactions,
    network,
    showLoadMore,
    isCardMissing,
    isCardLoading,
    isBalanceLoading: isBalanceResolving,
    isTxLoading: isTxResolving,
    txFilter,
    setTxFilter,
    filteredTransactions,
    currentTransaction,
    showTransactionDetail,
    openTransactionDetail,
    closeTransactionDetail,
    showQrCode,
    openQrCode,
    closeQrCode,
    copied,
    handleCopy,
  };
}
